import React, { FC, useEffect, useMemo } from "react";
import { DrawerComponent, Box, Flex, Text } from "components";
import { FiArrowLeft } from "react-icons/fi";
import { theme } from "@ovou-inc/ovou-ui";
import { BsPlus } from "react-icons/bs";
import { Button } from "@chakra-ui/react";
import { TagAction } from "../types/Tag";
import AddOrEditTag from "./AddOrEditTag";
import { TagList } from "./TagList";
import { ACTION } from "../../../constants";
import { useTagManagement } from "../hooks/useTagManagement";
import { IContact } from "../types/Contact";

interface IManageTagsProps {
  isOpen: boolean;
  onClose: () => void;
  contact?: IContact;
  refetchContactsData: () => void;
  teamId: number;
}

export const ManageTags: FC<IManageTagsProps> = ({
  isOpen,
  onClose,
  teamId,
  contact,
  refetchContactsData,
}) => {
  const {
    mode,
    checkedList,
    isTagItem,
    isLoading,
    handleTagsCheckboxChange,
    handleAssignTagsToContact,
    handleTagClick,
    handleAddNewTag,
    closeTagItem,
    isValidAssign,
    title,
    editable,
    tagsToShow,
    shouldShowCreateForm,
    isContactAssignmentMode,
    handleClose,
  } = useTagManagement({
    teamId,
    contact,
    refetchContactsData,
    onClose,
  });

  // Event listener for closing drawer
  useEffect(() => {
    const handleMessage = (event: MessageEvent<any>) => {
      if (event.data.close) {
        onClose();
      }
    };

    window.addEventListener("message", handleMessage, false);
    return () => {
      window.removeEventListener("message", handleMessage, false);
    };
  }, [onClose]);

  const renderAddNewTagButton = useMemo(() => {
    if (tagsToShow.length >= 10) return null;

    return (
      <Button
        borderRadius="6px"
        maxWidth="fit-content"
        background="white"
        boxShadow="none"
        height="32px"
        padding="12px"
        color={theme.palette.brand.primary.black}
        border={`1px solid ${theme.palette.ui.greys.grey3}`}
        leftIcon={
          <BsPlus color={theme.palette.brand.primary.orange} size="24px" />
        }
        _focus={{ outline: "none" }}
        sx={{
          _hover: {
            bg: "white",
            color: theme.palette.brand.primary.black,
          },
        }}
        _disabled={{ background: theme.palette.ui.greys.grey3 }}
        onClick={handleAddNewTag}
      >
        <Text fontSize={14} fontWeight="700" margin="0 5px">
          Add New Tag
        </Text>
      </Button>
    );
  }, [tagsToShow.length, handleAddNewTag]);

  return (
    <DrawerComponent isOpen={isOpen} onClose={onClose}>
      <Box sx={{ padding: "16px" }}>
        <FiArrowLeft
          cursor="pointer"
          color="#686868"
          size="16px"
          onClick={handleClose}
        />
        <Text fontSize={32} fontWeight="800" margin="12px 0">
          {title}
        </Text>
        <Text>
          Create and manage the tags your team uses to categorize their
          contacts.
        </Text>
        {isTagItem || shouldShowCreateForm ? (
          <AddOrEditTag
            mode={
              shouldShowCreateForm ? { action: ACTION.ADD as TagAction } : mode
            }
            teamId={teamId}
            closeTagItem={closeTagItem}
            refetchContactsData={refetchContactsData}
          />
        ) : (
          <Flex flexDirection="column" margin="20px 0" padding="0 16px 0 0">
            {isContactAssignmentMode ? (
              // Contact assignment mode - show checkboxes and assignment button
              <>
                <TagList
                  tags={tagsToShow}
                  checkedList={checkedList}
                  onTagClick={handleTagClick}
                  editable={editable}
                  onCheckboxChange={handleTagsCheckboxChange}
                  showCheckboxes={true}
                />
                {renderAddNewTagButton}

                <Button
                  variant="primary"
                  margin="33px 0 0"
                  onClick={handleAssignTagsToContact}
                  color={theme.palette.brand.primary.white}
                  isLoading={isLoading}
                  isDisabled={isLoading || !isValidAssign}
                >
                  Add Tags to Contact
                </Button>
              </>
            ) : (
              // General tag management mode - no checkboxes, no assignment button
              <>
                <TagList
                  tags={tagsToShow}
                  checkedList={[]}
                  onTagClick={handleTagClick}
                  editable={editable}
                  onCheckboxChange={undefined}
                  showCheckboxes={false}
                />

                {renderAddNewTagButton}
              </>
            )}
          </Flex>
        )}
      </Box>
    </DrawerComponent>
  );
};
